import {
  ConfigProvider as ProConfigProvider,
  ConfigConsumer as ProConfigConsumer,
  enUSIntl,
  zhCNIntl,
} from '@ant-design/pro-table';
import { ConfigProvider } from 'antd';
import type { Locale } from 'antd/lib/locale-provider';
import enUS from 'antd/lib/locale/en_US';
import moment from 'moment';
import React, { FC, useEffect } from 'react';
import zhCNAntd from '@/presentation/config/zh_CN';
import { AppLocale } from './index';
import { useLocale } from './use-locale';
import 'moment/locale/zh-cn';

const ANTD_LOCALES: Record<AppLocale, Locale> = {
  'zh-CN': zhCNAntd,
  'en-US': enUS,
};

// pro-components（pro-table/pro-form）的内置文案走自身 intl，不受 antd ConfigProvider.locale 控制，
// 需要显式注入对应语言的 intl，否则分页「总共 X 条」等文案会残留中文。
const PRO_INTLS: Record<AppLocale, typeof zhCNIntl> = {
  'zh-CN': zhCNIntl,
  'en-US': enUSIntl,
};

const MOMENT_LOCALES: Record<AppLocale, string> = {
  'zh-CN': 'zh-cn',
  'en-US': 'en',
};

// 跟随 i18next 当前语言，同步 antd 组件内置文案与 moment 日期 locale
export const LocaleProvider: FC = ({ children }) => {
  const { locale } = useLocale();

  useEffect(() => {
    moment.locale(MOMENT_LOCALES[locale] || 'zh-cn');
  }, [locale]);

  // 外层 antd ConfigProvider 负责 antd 组件（Table/Pagination 的「x 条/页」「跳至」等）；
  // 内层显式覆盖 pro-components 的 intl（按当前 locale 选择，不依赖 antd locale 的自动探测），
  // 使 pro-table/pro-form 的「查询/重置/请选择/总共 X 条」等默认文案跟随语言切换。
  const proIntl = PRO_INTLS[locale] || zhCNIntl;
  return (
    <ConfigProvider locale={ANTD_LOCALES[locale] || zhCNAntd}>
      <ProConfigConsumer>
        {value => (
          <ProConfigProvider value={{ ...value, intl: proIntl }}>
            {children}
          </ProConfigProvider>
        )}
      </ProConfigConsumer>
    </ConfigProvider>
  );
};
